import { Plan, Activity } from "@/features/planner/api/planner";

interface PhotographySpotsProps {
    plan: Plan;
}

export const PhotographySpots = ({ plan }: PhotographySpotsProps) => {
    // Helper to find photo activities
    const findPhotoSpots = (): Activity[] => {
        if (!plan || !plan.itinerary || !plan.itinerary.days) return [];

        const spots: Activity[] = [];
        const keywords = ["photo", "camera", "instagram", "picture", "scenic", "panorama", "skyline", "view", "sunset", "sunrise", "beautiful"];
        const titleKeywords = ["View", "Point", "Bridge", "Tower", "Square", "Park", "Lookout", "Deck"];

        plan.itinerary.days.forEach(day => {
            [day.morning, day.afternoon, day.evening].forEach(slot => {
                if (!slot) return;
                const activities = [...(slot.must_do || []), ...(slot.side_activities || [])];
                activities.forEach((act: Activity) => {
                    const text = `${act.title} ${act.why_special} ${act.insider_tip}`.toLowerCase();
                    const isPhotoSpot = keywords.some(k => text.includes(k)) || titleKeywords.some(k => act.title.includes(k));

                    if (isPhotoSpot) {
                        // Check for duplicates
                        if (!spots.find(s => s.title === act.title)) {
                            spots.push(act);
                        }
                    }
                });
            });
        });
        return spots.slice(0, 5); // Limit to top 5
    };

    const spots = findPhotoSpots();

    if (!spots || spots.length === 0) {
        return (
            <div className="max-w-[1000px] mx-auto p-10 bg-white">
                <h2 className="text-center text-[28px] font-bold text-[#187A85] mb-6 font-serif">
                    Photography Spots
                </h2>
                <div className="bg-[#FFF9F5] rounded-xl p-10 text-center flex flex-col items-center justify-center min-h-[200px]">
                    <span className="text-[48px] mb-4">📸</span>
                    <p className="text-[#222222] font-serif text-[18px] font-medium mb-2">
                        No specific photography spots identified.
                    </p>
                    <p className="text-[#666666] text-[14px]">
                        Explore the area and find your own hidden gems!
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="mx-5 p-10 bg-white">
            <h2 className="text-center text-[28px] font-bold text-[#187A85] mb-6 font-serif">
                Photography Spots
            </h2>

            <div className="flex flex-col gap-4">
                {spots.map((spot, index) => (
                    <div key={index} className="bg-[#FFF9F5] rounded-xl p-6">
                        <div className="text-[18px] font-bold text-[#187A85] mb-2">
                            {spot.title}
                        </div>

                        <div className="text-[14px] text-[#333333] mb-3 space-y-1">
                            <div className="flex items-start gap-2">
                                <span className="font-bold text-[#222] min-w-[90px]">Related to:</span>
                                <span>{spot.activity || spot.title}</span>
                            </div>

                            {/* Best Time if available */}
                            {spot.best_time && (
                                <div className="flex items-start gap-2">
                                    <span className="font-bold text-[#222] min-w-[90px]">Best Time:</span>
                                    <span className="text-[#E76828] font-semibold">{spot.best_time}</span>
                                </div>
                            )}
                        </div>

                        {/* Tip */}
                        <div className="bg-white p-4 rounded-lg border border-[#EAEAEA] flex items-start gap-3">
                            <span className="text-[18px]">💡</span>
                            <div className="text-[14px] text-[#555] italic leading-relaxed">
                                <span className="font-bold not-italic text-[#222] mr-1">Tip:</span>
                                {spot.insider_tip || spot.why_special || "Capture the moment!"}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
